import classNames from 'classnames';
import React, { useMemo } from 'react';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import SVGIcon from '../../components/SVGIcon';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import checkIcon from '../../icons/non-prefilled/16x16/check.svg';
import InvitationModal from '../../invitation/InvitationModal';
import { inputToOptions } from '../../invitation/InvitationUserSelect';
import useInvitationEmailOption from '../../invitation/useInvitationEmailOption';
import type { EntitySelectConfig } from '../../search/entitySearch/CategorizedEntitySelect';
import CategorizedEntitySelect from '../../search/entitySearch/CategorizedEntitySelect';
import topicPolicyCheck, {
  MEMBER_EDITABLE_NONE,
  TopicPolicyAction,
} from '../../topic/topicPolicyCheck';
import { UIActions } from '../../ui/uiSlice';
import usePublicContentPermissions from '../../utils/usePublicContentPermissions';
import * as styles from './SharingDropdownAddSelect.module.scss';

const POST_FRAGMENT = gql(`
  fragment SharingDropdownAddSelect_post on Post {
    id
    sharing {
      editorIds
      viewerIds
      editorGroupIds
      viewerGroupIds
    }
    topics {
      id
    }
    ...InvitationModal_initialPost
  }
`);

interface SharingDropdownAddSelectProps {
  onAddTopic: (topicId: string) => void;
  onAddUser: (userId: string) => void;
  onAddGroup: (groupId: string) => void;
  post: DocumentType<typeof POST_FRAGMENT>;
  autoFocus?: boolean;
}

const SharingDropdownAddSelect = ({
  onAddTopic,
  onAddUser,
  onAddGroup,
  post,
  autoFocus,
}: SharingDropdownAddSelectProps) => {
  const dispatch = useAppDispatch();

  const currentUser = useAppSelector((state) => state.session.user);
  const { isPermittedToPublicizePost } = usePublicContentPermissions();
  const getInvitationEmailOption = useInvitationEmailOption();

  const { sharing, topics } = post;

  const selectedIdSet = useMemo(
    () => new Set(topics?.map((topic) => topic.id)),
    [topics],
  );

  const config = useMemo<EntitySelectConfig>(() => {
    // TODO: specify topic type
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getTopicExtra = (topic: any) => {
      let disabledMessage = null;
      if (topic.id && selectedIdSet.has(topic.id)) {
        disabledMessage = (
          <>
            Already added to the post{' '}
            <SVGIcon inline size="small">
              {checkIcon}
            </SVGIcon>
          </>
        );
      }
      if (!topicPolicyCheck(TopicPolicyAction.EditPost, topic, currentUser)) {
        disabledMessage =
          topic.memberEditable === MEMBER_EDITABLE_NONE
            ? 'Restricted to owners'
            : 'Restricted to members';
      } else if (!isPermittedToPublicizePost && topic.privacy === 'PUBLIC') {
        disabledMessage = 'Only admins can add posts to public topics';
      }

      return {
        isDisabled: !!disabledMessage,
        secondary: disabledMessage,
      };
    };

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const topicSortBy = (l: any, r: any) => {
      const { isDisabled: isLDisabled } = getTopicExtra(l);
      const { isDisabled: isRDisabled } = getTopicExtra(r);

      if (isLDisabled === isRDisabled) {
        return 0;
      }

      return isLDisabled ? 1 : -1;
    };

    const userIdSet = new Set([
      ...(sharing?.editorIds || []),
      ...(sharing?.viewerIds || []),
    ]);

    const groupIdSet = new Set([
      ...(sharing?.editorGroupIds || []),
      ...(sharing?.viewerGroupIds || []),
    ]);

    return {
      topic: { sortBy: topicSortBy, extra: getTopicExtra },
      user: {
        extra: (user: { id: string }) => {
          const isDisabled = userIdSet.has(user.id);

          return {
            isDisabled,
            secondary: isDisabled ? 'Already added to the post' : null,
          };
        },
      },
      group: {
        extra: (group: { id: string }) => {
          const isDisabled = groupIdSet.has(group.id);

          return {
            isDisabled,
            secondary: isDisabled ? 'Already added to the post' : null,
          };
        },
      },
      new: {
        preferredMaxCount: 1,
        getGroup: (inputValue) =>
          getInvitationEmailOption({
            inputValue,
            onSelect: (suggestedEmail) => {
              dispatch(
                UIActions.openModal(InvitationModal, {
                  initialOptions: suggestedEmail
                    ? (inputToOptions(suggestedEmail) ?? undefined)
                    : undefined,
                  initialValues: { post },
                }),
              );
            },
          }),
      },
      order: ['new', 'user', 'group', 'topic'],
    };
  }, [
    sharing,
    selectedIdSet,
    currentUser,
    isPermittedToPublicizePost,
    getInvitationEmailOption,
    dispatch,
    post,
  ]);

  return (
    <CategorizedEntitySelect
      className={classNames(styles.select, styles.categorizedSelect)}
      controlShouldRenderValue={false}
      onChange={(value) => {
        if (!value) return;
        const { type, id } = value;
        if (type === 'user') onAddUser(id);
        if (type === 'topic') onAddTopic(id);
        if (type === 'group') onAddGroup(id);
      }}
      placeholder="Add topics, users, or email invite"
      config={config}
      // Ensure auto focus works when the component is hidden and then shown
      key={autoFocus ? 'auto-focus' : 'no-auto-focus'}
      autoFocus={autoFocus}
      value={null}
      aria-label="Add topics, users, or email invite"
    />
  );
};

export default SharingDropdownAddSelect;
